'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { getGPUTier } from 'detect-gpu';
import { Variants, motion } from 'framer-motion';

// Base frequencies for the noise
const BASE_FREQUENCIES = {
  small: {
    r: 0.00005,
    y: 0.001,
    b: 0.00005,
  },
  medium: {
    r: 0.00005,
    y: 0.001,
    b: 0.00005,
  },
  large: {
    r: 0.002,
    y: 0.005,
    b: 0.002,
  },
};

const NOISE_MATRIX_VALUES = {
  r: {
    small: `
    1   0   0   0   1
    0   0   0   0   0
    0   0   0   0   0
    0.5   -0.5   -0.5   1   0
    `,
    medium: `
    1   0   0   0   1
    0   0   0   0   0
    0   0   0   0   0
    0.5   -0.5   -0.5   1   0
    `,
    large: `
    1   0   0   0   1
    0   0   0   0   0
    0   0   0   0   0
    0   -0.3   -0.3   1   0
    `,
  },
  y: {
    small: `
    1  1  1  0  0 
    0  1  1  0  0 
    0  0  1  0  0 
    0  0  -0.5  1  0
    `,
    medium: `
    1  1  1  0  0 
    0  1  1  0  0 
    0  0  1  0  0 
    0  0  -0.5  1  0
    `,
    large: `
    1  1  1  0  0 
    0  1  1  0  0 
    0  0  1  0  0 
    0  0  0  1  0
    `,
  },
  b: {
    small: `
    0   0   0   0   0
    0   0   0   0   0
    0   0   1   0   1
    -0.5   -0.5   0.5   1   0
    `,
    medium: `
    0   0   0   0   0
    0   0   0   0   0
    0   0   1   0   1
    -0.5   -0.5   0.5   1   0
    `,
    large: `
    0   0   0   0   0
    0   0   0   0   0
    0   0   1   0   0
    -0.3   -0.3  0   1   0
    `,
  },
};

const BASE_INNER_SHADOW_VALUES = {
  small: 'shadow-[inset_0px_0px_15px_5px_rgba(0,_0,_0,_1)]',
  medium:
    'dark:shadow-[inset_0px_0px_30px_7px_rgba(0,_0,_0,_1)] shadow-[inset_0px_0px_35px_25px_rgba(0,_0,_0,_1)]',
  large: 'shadow-[inset_0px_0px_26px_13px_rgba(0,_0,_0,_1)]',
};

type GradientNoiseGlowProps = {
  enabled?: boolean;
  visible?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  borderRadiusClassName?: string;
  children: React.ReactNode;
} & React.PropsWithChildren;

const GradientNoiseGlow = ({
  className,
  visible = true,
  enabled = true,
  size = 'small',
  borderRadiusClassName = 'rounded-3xl',
  children,
}: GradientNoiseGlowProps) => {
  const [gradientContainerSize, setGradientContainerSize] = useState<number>(0);
  const gradientContainerRef = useRef<HTMLDivElement>(null);

  const [gpuTier, setGpuTier] = useState<number>(0);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    setIsSafari(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));
  }, []);

  useEffect(() => {
    async function checkGPUTier() {
      const gpuTier = await getGPUTier();
      setGpuTier(gpuTier.tier);
    }

    void checkGPUTier();
  }, []);

  // Set the gradient container size to the largest dimension of the container
  useEffect(() => {
    if (gradientContainerRef.current) {
      setGradientContainerSize(
        Math.max(
          gradientContainerRef.current.getBoundingClientRect().width,
          gradientContainerRef.current.getBoundingClientRect().height,
        ),
      );
    }

    window.addEventListener('resize', () => {
      if (gradientContainerRef.current) {
        setGradientContainerSize(
          Math.max(
            gradientContainerRef.current.getBoundingClientRect().width,
            gradientContainerRef.current.getBoundingClientRect().height,
          ),
        );
      }
    });
  }, []);

  // Set the seed values for the noise
  const seedR = useRef(Math.random() * 1000);
  const seedB = useRef(Math.random() * 1000);
  const seedY = useRef(Math.random() * 1000);

  useEffect(() => {
    if (!visible) {
      // Reset the seed values when the glow is disabled
      seedR.current = Math.random() * 1000;
      seedB.current = Math.random() * 1000;
      seedY.current = Math.random() * 1000;
    }
  }, [visible]);

  const variants: Variants = {
    hide: {
      opacity: 0,
    },
    show: {
      opacity: 1,
    },
    flash: {
      opacity: size === 'large' ? 0.3 : 1,
      transition: {
        repeat: 1,
        repeatType: 'reverse' as const,
        ease: 'easeInOut',
        duration: 0.6,
      },
    },
  };

  const getFilter = useMemo(() => {
    const FilterComponent = (color: 'r' | 'y' | 'b') => {
      return (
        <filter id={`noise-${color}`} width="100" height="100">
          <feTurbulence
            suppressHydrationWarning
            x="0"
            y="0"
            width={gradientContainerSize}
            height={gradientContainerSize}
            stitchTiles="stitch"
            id={`noiseTurbulence_${color}`}
            type="fractalNoise"
            baseFrequency={BASE_FREQUENCIES[size][color]}
            result={`NOISE_${color}`}
            numOctaves="3"
            seed={
              color === 'r'
                ? seedR.current
                : color === 'b'
                  ? seedB.current
                  : seedY.current
            }
          />
          <feColorMatrix
            in={`NOISE_${color}`}
            type="hueRotate"
            values="0"
            result={`NOISE_${color}`}
          >
            {!isSafari && gpuTier > 2 && (
              <animate
                attributeName="values"
                from="0"
                to="360"
                dur="2s"
                repeatCount="indefinite"
              />
            )}
          </feColorMatrix>

          <feComposite
            operator="in"
            in={`NOISE_${color}`}
            in2="SourceAlpha"
            result={`NOISE_${color}`}
          />
          <feColorMatrix
            in={`NOISE_${color}`}
            type="matrix"
            result={`NOISE_${color}`}
            values={NOISE_MATRIX_VALUES[color][size]}
          />
          <feComposite
            operator="in"
            in={`NOISE_${color}`}
            in2="SourceAlpha"
            result={`NOISE_${color}`}
          />
        </filter>
      );
    };
    FilterComponent.displayName = 'NoiseFilter';
    return FilterComponent;
  }, [gradientContainerSize, isSafari, size, gpuTier]);

  return (
    <>
      {!enabled ? (
        <>
          <div className={className} ref={gradientContainerRef}>
            {children}
          </div>
        </>
      ) : (
        <>
          <svg className="visible-hidden pointer-events-none fixed left-0 top-0">
            <defs>
              {getFilter('r')}
              {getFilter('y')}
              {getFilter('b')}
            </defs>
          </svg>

          <div className={className} ref={gradientContainerRef}>
            {/* border elements */}
            {[0, 1].map((i) => (
              <motion.div
                key={i}
                variants={variants}
                initial="hide"
                animate={visible ? 'show' : 'hide'}
                transition={{
                  type: 'spring',
                  stiffness: 150,
                  damping: 7,
                }}
                style={{
                  filter: `url(#noise-${i === 0 ? 'r' : 'b'})`,
                  animation:
                    gpuTier < 2
                      ? `slow-pulse ${i === 0 ? 2.5 : 3.5}s ease-in-out infinite`
                      : undefined,
                  willChange: 'opacity, filter',
                }}
                className={`pointer-events-none absolute left-0 top-0 z-10 h-full w-full border-2 border-white bg-transparent ${borderRadiusClassName}`}
              />
            ))}
            {/* white border */}
            <div
              className={`pointer-events-none absolute left-0 top-0 z-0 h-full w-full border border-white bg-transparent opacity-30 ${borderRadiusClassName}`}
            />

            {/* animated noise elements */}
            {[0, 1].map((i) => (
              <motion.div
                key={i}
                variants={variants}
                initial="hide"
                animate={visible ? 'show' : 'hide'}
                transition={{
                  type: 'spring',
                  stiffness: 150,
                  damping: 7,
                }}
                style={{
                  filter: `url(#noise-${i === 0 ? 'r' : 'b'})`,
                  animation:
                    gpuTier < 2
                      ? `slow-pulse ${i === 0 ? 2 : 3}s ease-in-out infinite`
                      : undefined,
                  willChange: 'opacity, filter',
                }}
                className={`pointer-events-none absolute left-0 top-0 h-full w-full ${borderRadiusClassName} ${BASE_INNER_SHADOW_VALUES[size]} `}
              />
            ))}

            {/* yellow noise element */}
            <motion.div
              variants={variants}
              initial="hide"
              animate={visible ? 'flash' : 'hide'}
              style={{
                filter: `url(#noise-y)`,
              }}
              className={`pointer-events-none absolute left-0 top-0 z-50 h-full w-full ${borderRadiusClassName} shadow-[inset_0px_0px_30px_35px_rgba(0,_0,_0,_0.5)]`}
            />

            {/* inner glow overlay */}
            <motion.div
              variants={variants}
              initial="hide"
              animate={visible ? 'show' : 'hide'}
              transition={{
                type: 'spring',
                stiffness: 150,
                damping: 7,
              }}
              style={{
                mixBlendMode: 'hard-light',
              }}
              className={`pointer-events-none absolute left-0 top-0 h-full w-full ${borderRadiusClassName} shadow-[inset_0px_0px_10px_5px_rgba(255,_255,_255,_0.1)]`}
            />
            {children}
          </div>
        </>
      )}
    </>
  );
};

export default GradientNoiseGlow;
